import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useApolloClient } from '@apollo/client';
import * as R from 'ramda';
import isEqual from 'lodash.isequal';
import { useSubscription } from '@apollo/client';
import { emptyFunction, emptyObject } from 'constants/emptyConst';
import { useParams } from 'react-router-dom';

const GlobalCacheManagerContext = createContext();

const GlobalCacheManagerProvider = ({ children }) => {
  const client = useApolloClient();
  const [, setGlobalSubscriptions] = useState([]);
  const [queryNamesForCacheEviction, setQueryNamesForCacheEviction] = useState([]);

  const registerGlobalSubscription = useCallback(
    gs => {
      if (gs.options?.skip) return;

      const getName = gs => gs.subscription.definitions[0].name.value;
      const getObservableSubscription = gs =>
        client.subscribe({ query: gs.subscription, variables: gs.options?.variables }).subscribe({ next: gs.options?.onSubscriptionData });

      setGlobalSubscriptions(prevGlobalSubscriptions => {
        if (R.any(x => !(x.globalKey === gs.globalKey))(prevGlobalSubscriptions)) {
          // Unsubscribe all global subscriptions
          R.forEach(x => x.observableSubscription.unsubscribe())(prevGlobalSubscriptions);

          // Evict from cache all global subscriptions that were unsubscribed.
          R.forEach(gs => {
            const keysToEvict = Object.keys(client.cache.data.data?.ROOT_SUBSCRIPTION || emptyObject).filter(x =>
              x.startsWith(getName(gs))
            );
            R.forEach(key => {
              client.cache.evict({ id: 'ROOT_SUBSCRIPTION', fieldName: key, broadcast: false });
            })(keysToEvict);
          })(prevGlobalSubscriptions);

          // Evict from cache all queries registered on offer level for cache clearing.
          const queryKeysToEvict = Object.keys(client.cache.data.data?.ROOT_QUERY || emptyObject).filter(queryKey =>
            R.any(queryName => queryKey.startsWith(queryName))(queryNamesForCacheEviction)
          );
          R.forEach(key => {
            client.cache.evict({ id: 'ROOT_QUERY', fieldName: key, broadcast: false });
          })(queryKeysToEvict);

          // Call cache gc
          client.cache.gc();
          return [{ ...gs, observableSubscription: getObservableSubscription(gs) }];
        }

        if (R.any(x => getName(x) === getName(gs) && isEqual(x.options, gs.options))(prevGlobalSubscriptions)) {
          return prevGlobalSubscriptions;
        }

        if (R.any(x => x.id === gs.id)(prevGlobalSubscriptions)) {
          prevGlobalSubscriptions.find(x => x.id === gs.id).observableSubscription.unsubscribe();
          const newGlobalSubscriptions = prevGlobalSubscriptions.filter(x => x.id !== gs.id);
          return [...newGlobalSubscriptions, { ...gs, observableSubscription: getObservableSubscription(gs) }];
        }

        return [...prevGlobalSubscriptions, { ...gs, observableSubscription: getObservableSubscription(gs) }];
      });
    },
    [client, queryNamesForCacheEviction]
  );

  const registerQueryForCacheEviction = useCallback(
    query => {
      const queryName = query.definitions[0].name.value;
      if (R.any(q => q === queryName)(queryNamesForCacheEviction)) return;
      setQueryNamesForCacheEviction([...queryNamesForCacheEviction, queryName]);
    },
    [queryNamesForCacheEviction]
  );

  const actions = useMemo(
    () => ({ registerGlobalSubscription, registerQueryForCacheEviction }),
    [registerGlobalSubscription, registerQueryForCacheEviction]
  );

  return <GlobalCacheManagerContext.Provider value={actions}>{children}</GlobalCacheManagerContext.Provider>;
};

GlobalCacheManagerProvider.propTypes = {
  children: PropTypes.element.isRequired
};

const useGlobalCacheManager = () => useContext(GlobalCacheManagerContext);

const useGlobalSubscription = (subscription, options, onGlobalSubscriptionData) => {
  const [id] = useState(crypto.randomUUID());
  const { documentId, siteId } = useParams();
  const { registerGlobalSubscription } = useGlobalCacheManager();

  const globalSubscription = useMemo(
    () => ({
      id,
      globalKey: `${documentId ?? 0}-${siteId ?? 0}`,
      subscription,
      options: { ...options, onSubscriptionData: onGlobalSubscriptionData ?? emptyFunction }
    }),
    [id, documentId, siteId, subscription, options, onGlobalSubscriptionData]
  );

  useEffect(() => {
    if (documentId == null || siteId == null) {
      return;
    }
    registerGlobalSubscription(globalSubscription);
  }, [globalSubscription, registerGlobalSubscription, documentId, siteId]);

  return useSubscription(subscription, options);
};

export { GlobalCacheManagerProvider, useGlobalCacheManager, useGlobalSubscription };
